// Company Compliance monitoring (Model A: company-admin surface inside main-app).
// A tenant admin / HR sees which legal documents are in force, how much of their
// workforce has accepted each one, who is still outstanding, and an audit log of
// acceptances. Read-only — publishing legal documents is a platform operation
// (admin-portal Compliance Center). Data is tenant-scoped server-side and
// restricted to privileged roles (the API 403s a plain employee).
import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, FileText, CheckCircle2, AlertTriangle, Loader2, Info } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { getAllEmployees, type Employee } from '@/lib/hrApi';
import {
  getTenantAcceptances,
  getTenantSummary,
  type TenantAcceptance,
  type ComplianceSummaryRow,
} from '@/services/complianceApi';

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const docTitle = (slug: string, fallback?: string) =>
  fallback || slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const CompliancePage: React.FC = () => {
  const [summary, setSummary] = useState<ComplianceSummaryRow[]>([]);
  const [acceptances, setAcceptances] = useState<TenantAcceptance[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, a, emps] = await Promise.all([
          getTenantSummary(),
          getTenantAcceptances(),
          getAllEmployees().catch(() => [] as Employee[]),
        ]);
        if (!active) return;
        setSummary(s);
        setAcceptances(a);
        setEmployees(emps);
      } catch (e: any) {
        if (active) {
          setError(
            e?.response?.status === 403
              ? "You don't have permission to view company compliance."
              : 'Could not load compliance data.',
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const empByUser = useMemo(() => {
    const m = new Map<string, Employee>();
    for (const e of employees) if (e.user_id) m.set(e.user_id, e);
    return m;
  }, [employees]);

  const nameForUser = (userId?: string | null) => {
    if (!userId) return 'Unknown user';
    const e = empByUser.get(userId);
    return e ? `${e.first_name} ${e.last_name}`.trim() || e.email : userId.slice(0, 8);
  };

  const headcount = employees.length;

  // Per required doc: the set of user_ids that accepted at the current version.
  const acceptedByDoc = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const row of summary) {
      const set = new Set<string>();
      for (const a of acceptances) {
        if (a.doc_type === row.doc_type && a.version >= row.version && a.user_id) set.add(a.user_id);
      }
      m.set(row.doc_type, set);
    }
    return m;
  }, [summary, acceptances]);

  // Employees who still owe acceptance on at least one required document.
  const outstanding = useMemo(() => {
    const rows: Array<{ employee: Employee; missing: string[] }> = [];
    for (const e of employees) {
      if (!e.user_id) continue;
      const missing = summary
        .filter((row) => !acceptedByDoc.get(row.doc_type)?.has(e.user_id!))
        .map((row) => docTitle(row.doc_type, row.title));
      if (missing.length) rows.push({ employee: e, missing });
    }
    return rows;
  }, [employees, summary, acceptedByDoc]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading compliance…
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-warning" />
        {error}
      </div>
    );
  }

  const recent = acceptances.slice(0, 25);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
          <ShieldCheck className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-h2 text-charcoal">Compliance</h1>
          <p className="text-muted-foreground mt-0.5 text-sm">
            How much of your team has accepted each required policy.
          </p>
        </div>
      </div>

      {/* Scope banner — make it unmistakable this is a read-only monitoring view. */}
      <div className="flex items-start gap-2 rounded-xl border border-orange-100 bg-orange-50/60 px-4 py-3 text-sm text-muted-foreground dark:border-orange-500/20 dark:bg-orange-500/[0.06]">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-500" />
        <span>
          The legal policies themselves are set and maintained by KaziPeople. This
          page is read-only — it shows your team's acceptance so you can track who's
          covered and who's still outstanding.
        </span>
      </div>

      {/* Per-document acceptance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {summary.map((row) => {
          const accepted = acceptedByDoc.get(row.doc_type)?.size ?? row.accepted_count;
          const pct = headcount ? Math.round((accepted / headcount) * 100) : 0;
          const complete = headcount > 0 && accepted >= headcount;
          return (
            <Card key={row.doc_type} className="border-orange-100 dark:border-orange-500/20">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground flex-1">
                    {docTitle(row.doc_type, row.title)}
                  </span>
                  <span className="text-xs font-medium text-muted-foreground">v{row.version}</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold text-foreground">{accepted}</span>
                  <span className="text-sm text-muted-foreground">
                    / {headcount || '—'} accepted
                  </span>
                  {complete && <CheckCircle2 className="w-4 h-4 text-success ml-auto" />}
                </div>
                <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full ${complete ? 'bg-success' : 'bg-orange-500'}`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
        {summary.length === 0 && (
          <div className="col-span-full py-10 text-center text-muted-foreground text-sm">
            No required documents are configured yet.
          </div>
        )}
      </div>

      {/* Outstanding acceptances */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-warning" />
            <h2 className="font-semibold text-foreground">Outstanding</h2>
            <span className="text-xs text-muted-foreground">
              {outstanding.length} {outstanding.length === 1 ? 'person' : 'people'}
            </span>
          </div>
          {outstanding.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-success" />
              Everyone is up to date.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {outstanding.map(({ employee, missing }) => (
                <div key={employee.id} className="py-2.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {employee.first_name} {employee.last_name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{employee.email}</div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {missing.map((m) => (
                      <span
                        key={m}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-warning-soft text-warning-strong"
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Acceptance log */}
      <Card>
        <CardContent className="p-5">
          <h2 className="font-semibold text-foreground mb-4">Recent acceptances</h2>
          {recent.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No acceptances yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Person</th>
                    <th className="py-2 pr-4 font-medium">Document</th>
                    <th className="py-2 pr-4 font-medium">Version</th>
                    <th className="py-2 pr-4 font-medium">When</th>
                    <th className="py-2 font-medium">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4 text-foreground">{nameForUser(a.user_id)}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{docTitle(a.doc_type)}</td>
                      <td className="py-2 pr-4 text-muted-foreground">v{a.version}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{fmtDate(a.accepted_at)}</td>
                      <td className="py-2 text-muted-foreground">{a.source || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompliancePage;
